import { useTheme, useMediaQuery } from "@material-ui/core";
import { includes, uniqBy } from "lodash";
import * as React from "react";
import { FETCH_USER_INFO } from "../../Fedx";
import { SessionCache } from "../../helpers/cache";
import { isArrayValid } from "../../helpers/utility";
import { useHubx } from "../../HubX";
import { APP_DEFAULT_ENV_STR, WM_INTERNAL_GROUP_NAME } from "../constants/appGlobals";
import { BLOCK_FEATURE_ON_MOBILE_PERMISSIONS } from "../constants/blockedMobileFeatures";
import WMInternalAccessPermission from "../constants/WMInternalAccess";
import {
  APP_PERMISSIONS,
  PermissionsType,
  PERMISSION_ACCESS_ADMIN,
  PERMISSION_ACCESS_ALL_INSTALLATIONS,
  PERMISSION_ACCESS_FREE_USER,
} from "../models/Permission";
import User from "../models/User";
import { ADMIN, SUPER_ADMIN } from "../models/GroupTypeEnum";
import CustomerModel from "../models/Customer";
import { INTERNAL } from "../models/GroupTypeEnum";

type useAuthType = {
  set: (token: string | null) => void;
  get: () => boolean;
  hasInstallationInSomeGroup: (installationId: string | number) => boolean;
  hasPermission: (permissionType: PermissionsType, group?: CustomerModel | undefined) => boolean;
  hasAnyPermission: (
    permissionToCheck: PermissionsType[],
    originalCustomers?: CustomerModel[] | undefined
  ) => boolean;
  hasHighLevel: (permissionType?: PermissionsType) => boolean;
  mobileRestriction: (permissionToCheck: PermissionsType[]) => boolean;
  updateUserDefinitions: () => void;
  isInWMInternalGroup: boolean;
  isOnlyInWMInternalGroup: boolean;
  isFreeUser: boolean;
};

declare global {
  interface Window {
    HAS_CONFIGURED_G_ANALYTICS: string | undefined;
    gtag: any;
  }
}

const useAuth = (): useAuthType => {
  const cache = new SessionCache();
  const initialAuth = cache.get("bearer-token") ? true : false;
  const storedUser: User | null = cache.get("user");
  const hub = useHubx();
  const theme = useTheme();
  const isMobileResolution = useMediaQuery(theme.breakpoints.down("xs"));


  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        const UserFromAPI = await hub.request(FETCH_USER_INFO);
        cache.set("user", UserFromAPI);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("useAuth → fetchUser -> error", error);
      }
    };
    if (!isArrayValid(storedUser?.installationGroup)) {
      fetchUser();
    }

    if (!window?.HAS_CONFIGURED_G_ANALYTICS) {
      let custom_props = {};

      if (storedUser) {
        window.HAS_CONFIGURED_G_ANALYTICS = storedUser?.email;
        custom_props = {
          user_name: storedUser?.name,
          user_email: storedUser?.email,
          environment: process.env.REACT_APP_LOG_ENV ?? APP_DEFAULT_ENV_STR,
        };

        window?.gtag("config", "G-DYP94VHEK1", {
          ...custom_props,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedUser]);

  const updateUserDefinitions = () => {
    try {
      hub.request(FETCH_USER_INFO).then((userData) => {
        cache.set("user", userData);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("useAuth → fetchUser -> error", error);
    }
  };

  const isInWMInternalGroup = React.useMemo(() => {
    return !!storedUser?.installationGroup?.find((group) => {
      return (
        group.groupType === INTERNAL &&
        group.active &&
        group.name === WM_INTERNAL_GROUP_NAME &&
        group.permissionList
          ?.map((permission) => permission.type)
          .includes(PERMISSION_ACCESS_ALL_INSTALLATIONS)
      );
    });
  }, [storedUser]);

  const onAdminGroup = React.useMemo(() => {
    return !!storedUser?.installationGroup?.find((group) => {
      return (
        group.active &&
        (group.groupType == ADMIN || group.groupType == SUPER_ADMIN) &&
        group.permissionList?.map((permission) => permission.type).includes(PERMISSION_ACCESS_ADMIN)
      );
    });
  }, [storedUser]);

  const isOnlyInWMInternalGroup =
    isInWMInternalGroup && storedUser?.installationGroup?.length === 1;

  const isFreeUser = React.useMemo(() => {
    return !!storedUser?.installationGroup?.some((group) =>
      group.permissionList?.some((permission) => permission.type === PERMISSION_ACCESS_FREE_USER)
    );
  }, [storedUser]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAuth] = React.useState(initialAuth);

  const set = (token: string | null) => {
    cache.set("bearer-token", token);
    setAuth(Boolean(token));
  };

  const get = () => {
    return cache.get("bearer-token") ? true : false;
  };

  const getPermissions = React.useCallback(
    (group: CustomerModel | CustomerModel[] | undefined = undefined) => {
      const isSuperAdmin = storedUser?.email === "admin@admin.com.br";

      if (isSuperAdmin) {
        return APP_PERMISSIONS.map((x) => {
          return { type: x };
        });
      }

      let permissions = null;

      if (group && !isArrayValid(group)) {
        const newGroup = group as CustomerModel;
        const groupWithPermission = storedUser?.installationGroup?.find(
          (x) => x.id == newGroup?.customer_id
        );
        permissions = groupWithPermission?.permissionList;
      } else if (isArrayValid(group)) {
        const newGroup = group as CustomerModel[];
        const groupsWithPermission = newGroup?.map((e) => {
          return storedUser?.installationGroup?.find((s) => s.id == e.customer_id);
        });
        permissions = groupsWithPermission?.flatMap((groupAux) => {
          return groupAux?.permissionList ?? [];
        });
      } else {
        permissions = storedUser?.installationGroup?.flatMap((group) => {
          return group?.permissionList ?? [];
        });
      }
      // sair
      return uniqBy(permissions, (x) => x?.type ?? "") ?? [];
    },
    [storedUser]
  );

  const hasPermission = (
    permissionType: PermissionsType,
    group: CustomerModel | undefined = undefined
  ) => {
    if (permissionType === 0) {
      return get();
    }

    let permissions = null;

    if (isInWMInternalGroup && WMInternalAccessPermission.includes(permissionType)) return true;

    if (onAdminGroup) return true;

    if (group) {
      permissions = getPermissions(group);
    } else {
      permissions = getPermissions();
    }

    if (!permissions) {
      return false;
    }

    return includes(
      permissions.map((x) => x?.type),
      permissionType
    );
  };

  const hasHighLevel = (permissionType?: PermissionsType) => {
    if (permissionType) {
      if (isInWMInternalGroup && WMInternalAccessPermission.includes(permissionType)) return true;
    }
    if (isInWMInternalGroup) return true;

    if (onAdminGroup) return true;

    return false;
  };

  const hasAnyPermission = (
    permissionToCheck: PermissionsType[],
    originalCustomers?: CustomerModel[] | undefined
  ) => {
    let permissions = null;
    if (originalCustomers) {
      permissions = getPermissions(originalCustomers);
    } else {
      permissions = getPermissions();
    }
    const permissionsTypes = permissions?.map((x) => x.type);

    return (
      permissionToCheck.some((permission) => permissionsTypes.includes(permission)) ||
      (isInWMInternalGroup &&
        permissionToCheck.some((permission) => WMInternalAccessPermission.includes(permission)))
    );
  };

  const mobileRestriction = (permissionToCheck: PermissionsType[]) => {
    if (!isMobileResolution) {
      return false;
    }

    return permissionToCheck.every((permission) =>
      BLOCK_FEATURE_ON_MOBILE_PERMISSIONS.includes(permission)
    );
  };

  const hasInstallationInSomeGroup = (installationId: string | number) => {
    const allInstallationsIds = storedUser?.installationGroup?.flatMap((x) =>
      x.installationList?.flatMap((installation) => String(installation.installationId))
    );

    return Boolean(allInstallationsIds?.includes(String(installationId)));
  };

  return {
    get,
    set,
    hasPermission,
    hasAnyPermission,
    mobileRestriction,
    hasInstallationInSomeGroup,
    updateUserDefinitions,
    isInWMInternalGroup,
    isOnlyInWMInternalGroup,
    hasHighLevel,
    isFreeUser,
  };
};

export default useAuth;
